import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, XIcon, PlayIcon, Maximize2, X } from 'lucide-react';
import { ColumviLogo } from './ColumviLogo';
import { ColumviBackground } from './ColumviBackground';
interface ColumviResultSlideProps {
  isCorrect: boolean;
  correctAnswer: string;
  peerPercentage: number;
  insightHeadline: string;
  insightBody: string[];
  visualUrl: string;
  ctaLabel: string;
  onCta?: () => void;
  expandedHeadline?: string;
  expandedBody?: string[];
}
export function ColumviResultSlide({
  isCorrect,
  correctAnswer,
  peerPercentage,
  insightHeadline,
  insightBody,
  visualUrl,
  ctaLabel,
  onCta,
  expandedHeadline,
  expandedBody
}: ColumviResultSlideProps) {
  const [isVideoExpanded, setIsVideoExpanded] = useState(false);
  const videoUrl = 'https://cdn.magicpatterns.com/uploads/c7XQfNarvLqN3SkpxWNpv4/videoplayback (8).mp4';
  return <ColumviBackground data-id="element-62">
      <div className="flex flex-col h-full overflow-y-auto relative" data-id="element-63">
        <div className="px-5 pt-6 pb-5 flex flex-col min-h-full" data-id="element-64">
          {/* Header: Logo + Feedback Pill */}
          <div className="flex items-center justify-between mb-6" data-id="element-65">
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5
          }} data-id="element-66">
              <ColumviLogo size="sm" variant="light" data-id="element-67" />
            </motion.div>

            {/* Correct / Not Quite Pill - Kept at Top */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.4,
            delay: 0.2
          }} data-id="element-68">
              {isCorrect ? <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-400/30 backdrop-blur-sm" data-id="element-69">
                  <CheckIcon className="w-3.5 h-3.5 text-green-300" data-id="element-70" />
                  <span className="text-green-200 text-xs font-medium" style={{
                fontFamily: 'var(--font-gotham)'
              }} data-id="element-71">
                    Correct — {correctAnswer}
                  </span>
                </div> : <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/30 backdrop-blur-sm" data-id="element-72">
                  <XIcon className="w-3.5 h-3.5 text-amber-300" data-id="element-73" />
                  <span className="text-amber-200 text-xs font-medium" style={{
                fontFamily: 'var(--font-gotham)'
              }} data-id="element-74">
                    Not quite — {correctAnswer}
                  </span>
                </div>}
            </motion.div>
          </div>

          {/* SECTION 1 — Top Image Banner */}
          <motion.div className="relative rounded-2xl overflow-hidden shadow-lg mb-5" initial={{
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-75">
            <img src={visualUrl} alt="Clinical visual" className="w-full h-40 object-cover object-center" data-id="element-76" />
            {/* Soft gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-77" />
          </motion.div>

          {/* SECTION 3 — Poll Line */}
          <motion.div className="mb-6" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.6
        }} data-id="element-78">
            <p className="text-white/50 text-xs mb-2" style={{
            fontFamily: 'var(--font-gotham)'
          }} data-id="element-79">
              {peerPercentage}% of professionals answered like you.
            </p>
            <div className="h-1 rounded-full bg-white/10 overflow-hidden" data-id="element-80">
              <motion.div className="h-full rounded-full" style={{
              background: '#E76F24'
            }} initial={{
              width: 0
            }} animate={{
              width: `${peerPercentage}%`
            }} transition={{
              duration: 1,
              delay: 0.8,
              ease: 'easeOut'
            }} data-id="element-81" />
            </div>
          </motion.div>

          {/* SECTION 4 — Insight Block */}
          <motion.div className="mb-6" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} data-id="element-82">
            <h2 className="text-white text-lg font-bold mb-3" style={{
            fontFamily: 'var(--font-gotham)'
          }} data-id="element-83">
              {insightHeadline}
            </h2>
            {insightBody.map((text, i) => <p key={i} className="text-white/70 text-sm leading-relaxed mb-2" style={{
            fontFamily: 'var(--font-gotham)'
          }} data-id="element-84">
                {text}
              </p>)}
          </motion.div>

          {/* SECTION 5 — Video Card (Bottom Area) */}
          <motion.div className="mt-auto mb-5" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.9
        }} data-id="element-85">
            <p className="text-white/50 text-xs mb-2 font-medium" data-id="element-86">
              Watch mechanism of action
            </p>
            <div className="relative w-full h-24 rounded-xl overflow-hidden bg-black border border-white/10 cursor-pointer group" onClick={() => setIsVideoExpanded(true)} data-id="element-87">
              {/* Video Thumbnail / Preview - Removed opacity for better visibility */}
              <video src={videoUrl} className="w-full h-full object-cover" muted loop autoPlay playsInline data-id="element-88" />

              {/* Play Button Overlay */}
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors" data-id="element-89">
                <div className="w-10 h-10 rounded-full bg-[#E76F24] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform" data-id="element-90">
                  <PlayIcon className="w-5 h-5 text-white ml-0.5" fill="white" data-id="element-91" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* SECTION 6 — Primary CTA Button */}
          <motion.button className="w-full py-3.5 rounded-xl text-white text-sm font-semibold transition-all duration-200" style={{
          fontFamily: 'var(--font-gotham)',
          background: '#E76F24',
          boxShadow: '0 4px 15px rgba(231,111,36,0.3)'
        }} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.1
        }} whileHover={{
          scale: 1.02,
          boxShadow: '0 6px 20px rgba(231,111,36,0.4)'
        }} whileTap={{
          scale: 0.98
        }} onClick={onCta} data-id="element-92">
            {ctaLabel}
          </motion.button>
        </div>

        {/* Expanded Video Overlay */}
        <AnimatePresence data-id="element-93">
          {isVideoExpanded && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 z-50 bg-black/95 backdrop-blur-sm flex flex-col overflow-y-auto" data-id="element-94">
              {/* Close Button */}
              <motion.button className="absolute top-4 right-4 z-10 p-2 text-white/70 hover:text-white" onClick={e => {
            e.stopPropagation();
            setIsVideoExpanded(false);
          }} whileHover={{
            scale: 1.1
          }} whileTap={{
            scale: 0.9
          }} data-id="element-95">
                <X className="w-6 h-6" data-id="element-96" />
              </motion.button>

              <div className="flex flex-col px-5 pt-14 pb-6" data-id="element-97">
                {/* Video Player */}
                <motion.div initial={{
              scale: 0.95,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} exit={{
              scale: 0.95,
              opacity: 0
            }} className="w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl border border-white/10" onClick={e => e.stopPropagation()} data-id="element-98">
                  <video src={videoUrl} className="w-full h-full object-contain" controls autoPlay playsInline data-id="element-99" />
                </motion.div>

                {/* Video Caption */}
                <motion.p initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-white/40 text-xs mt-3 mb-8" style={{
              fontFamily: 'var(--font-gotham)'
            }} data-id="element-100">
                  Bispecific T-cell engagement mechanism
                </motion.p>

                {/* Insight Headline */}
                <motion.h2 initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-white text-xl leading-snug font-bold mb-6" style={{
              fontFamily: 'var(--font-gotham)'
            }} data-id="element-101">
                  {expandedHeadline || insightHeadline}
                </motion.h2>

                {/* Supporting Text */}
                {(expandedBody || insightBody).map((text, i) => <motion.p key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4 + i * 0.15
            }} className="text-white/70 text-sm leading-relaxed mb-4" style={{
              fontFamily: 'var(--font-gotham)'
            }} data-id="element-102">
                    {text}
                  </motion.p>)}

                {/* CTA Button */}
                <motion.button initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.7
            }} className="w-full py-3.5 rounded-xl text-white text-sm font-semibold mt-4" style={{
              fontFamily: 'var(--font-gotham)',
              background: '#E76F24',
              boxShadow: '0 4px 15px rgba(231,111,36,0.3)'
            }} onClick={e => {
              e.stopPropagation();
              setIsVideoExpanded(false);
              onCta?.();
            }} data-id="element-103">
                  {ctaLabel}
                </motion.button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </ColumviBackground>;
}